/**
 * THE QUEUE'S FIVE GROUPS: what the reader owes, not where the work stands.
 *
 *   Needs you       an open decision on the ticket (`rowStateOf` waiting)
 *   Ready to merge  the Team is finished; only a person's approval or merge
 *                   is left (`isReadyToMerge`), or GitHub approved the pull
 *                   request and nothing is asked (`isApprovedReadyToMerge`)
 *   Working         an agent is moving it, or it is honestly waiting its turn
 *   Stopped         it ended badly, or something other than the machine holds
 *                   it, including a build that stopped and then asked
 *                   (`isStoppedAsk`)
 *   Done            every settled ticket, newest first, paged by the route;
 *                   one closed with no work landed says CLOSED, not DONE
 *                   (`closedRowWords`)
 *
 * Every rule here is `workStage.ts`'s: `rowStateOf` decides the group and a
 * queued row follows `stagePieOf`'s own rule (a wait for a free run slot keeps
 * the calm ring and prints in Working; any other hold is amber and prints in
 * Stopped). The fold into one row per ticket is `workViews.foldPipelineRows`.
 *
 * A split's tickets print under their parent's row (`nestSplits`, YAG-1113),
 * the family once, in the group that most needs the reader among them.
 *
 * Off-ticket work (Proctor reviewing a pull request nobody filed a ticket
 * for) prints only while it waits on a person; the rest is "Also today",
 * which a later change adds under Done.
 */
import type { WorkerSummary, WorkItem, WorkItemRunListEntry, WorkSearchQuery } from "@yagni/shared";
import {
  clockTotalsOf,
  isFletcherRequestItem,
  liveSplitOf,
  readyAskNeedsApproval,
  splitLeadOf,
  workSearchReasonOf,
} from "@yagni/shared";
import { readSplitPlace, splitPartPlaceOf, type SplitPartPlace } from "../work/splitPlace.js";
import {
  gateAskOf,
  isApprovedReadyToMerge,
  isNeedsYou,
  isFixingInReview,
  isReadyToMerge,
  isSettledInfraStop,
  isStoppedAsk,
  isWaitingOnViewer,
  PIPELINE_STAGE_CRAFT,
  PIPELINE_STAGES,
  pipelineStageOf,
  qaAsksPerson,
  qaResultWords,
  rowAgeOf,
  rowClauseOf,
  rowStateLine,
  rowStateOf,
  stageLabelOf,
  stageOutcomeOf,
  stagePieOf,
} from "../work/workStage.js";
import {
  activityAtOf,
  driverOf,
  foldPipelineRows,
  waitingSinceOf,
  workingItemsOf,
  type PipelineRow,
} from "../work/workViews.js";
import type { StageOwnerLookup } from "../work/useWorkerLookup.js";

export type QueueGroup = "needs" | "ready" | "live" | "stop" | "done";

export const QUEUE_GROUPS: readonly QueueGroup[] = ["needs", "ready", "live", "stop", "done"];

export const QUEUE_GROUP_LABEL: Record<QueueGroup, string> = {
  needs: "Needs you",
  ready: "Ready to merge",
  live: "Working",
  stop: "Stopped",
  done: "Done",
};

/** How many finished tickets one page of Done holds (the Pull request reviews list's size). */
export const TEAM_DONE_PAGE_SIZE = 20;

/** The word on a row's hover button. Rows never commit; this only names what opening it is for. */
export type RowVerb =
  | "Answer"
  | "Review"
  | "Approve"
  | "Merge"
  | "Watch"
  | "Open"
  | "Why"
  | "Receipt";

function timeOf(iso: string | null | undefined): number {
  const at = iso ? Date.parse(iso) : Number.NaN;
  return Number.isFinite(at) ? at : 0;
}

function startOfDay(now: number): number {
  const day = new Date(now);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
}

/** Proctor's review of a pull request no ticket on this Team commissioned. */
export function isStandaloneReview(row: PipelineRow): boolean {
  return row.anchor.kind === "review" && !isFletcherRequestItem(row.anchor);
}

const PROCTOR_FOUND_PROBLEMS = "found problems";

/** What Proctor's review of the pull request concluded, by `code_review_pr_state.agent_review_state`. */
const PROCTOR_RESULT_WORDS: Readonly<Record<string, string>> = {
  approved: "approved",
  approval_advised: "would approve",
  comments_posted: "reviewed",
  needs_follow_up: PROCTOR_FOUND_PROBLEMS,
};

/**
 * A one-attempt lane check that tested a head the pull request has since
 * moved past: what it found is about code that is gone, so it is not the
 * ticket's result. Only the lane's row carries the pull request's head.
 */
function testedAnOlderHead(anchor: WorkItem, check: WorkItem): boolean {
  if (anchor.lane !== "attempt" || !anchor.commitSha || !check.qaHeadSha) return false;
  const [a, b] = [anchor.commitSha.toLowerCase(), check.qaHeadSha.toLowerCase()];
  return !(a.startsWith(b) || b.startsWith(a));
}

/** What each check on a ticket's pull request found, once the results are in. */
export interface CheckResults {
  /** Proctor's verdict in words, or null when no review has concluded. */
  proctor: string | null;
  /** Fletcher's outcome in words (passed, found problems, blocked), or null when no check finished. */
  fletcher: string | null;
  /** The check's result asks a person: it found problems, or it could not test. */
  asksPerson: boolean;
}

/**
 * The checks' results on a ticket in Review that nothing is working on and
 * nobody has been asked about: what Proctor concluded and what Fletcher
 * found. Null while anything is in flight, when the ticket holds an ask of
 * its own (its sentence is the truer one), or when no check has finished.
 */
export function checkResultsOf(row: PipelineRow): CheckResults | null {
  if (isStandaloneReview(row) || workingItemsOf(row).length > 0) return null;
  const anchor = row.anchor;
  // Nothing asked and nothing stopped: the ticket, or Proctor's idle case
  // speaking for it, is only waiting on what the checks found.
  const driver = driverOf(row);
  if (driver === row.qa || rowStateOf(driver) !== "queued") return null;
  if (rowStateOf(anchor) !== "queued" || pipelineStageOf(anchor) !== "review") return null;
  const qa = row.qa && !testedAnOlderHead(anchor, row.qa) ? row.qa : null;
  const fletcher = qa ? qaResultWords(qa) : null;
  if (!qa || !fletcher) return null;
  const state = anchor.pullRequest?.proctorState ?? row.review?.pullRequest?.proctorState ?? null;
  const proctor =
    (state ? PROCTOR_RESULT_WORDS[state] : undefined) ??
    (row.review && stageOutcomeOf(row.review) === "failed" ? PROCTOR_FOUND_PROBLEMS : null);
  return { proctor, fletcher, asksPerson: qaAsksPerson(qa) };
}

/**
 * The row's line once the checks' results are in: "Proctor: would approve ·
 * Fletcher: found problems". Each name is the Agent the directory names for
 * the craft, else the Agent's own name.
 */
export function checkResultsLine(
  results: CheckResults,
  names: { reviewer: string | null; tester: string | null },
): string {
  return [
    results.proctor ? `${names.reviewer ?? "Proctor"}: ${results.proctor}` : null,
    results.fletcher ? `${names.tester ?? "Fletcher"}: ${results.fletcher}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * A live split speaking through its tickets (YAG-1111): their progress, while
 * it has no ask of its own. Its row rolls them up and only opens the parent;
 * every act is on the ticket that asks.
 */
function splitRollUpOf(item: WorkItem) {
  return isNeedsYou(item) ? null : liveSplitOf(item);
}

/**
 * Which group a row prints in, or null when it prints in none (off-ticket work
 * that does not wait on anyone).
 */
export function queueGroupOf(row: PipelineRow, now: number): QueueGroup | null {
  const driver = driverOf(row);
  const state = rowStateOf(driver);
  if (isStandaloneReview(row) && state !== "waiting") return null;
  // Fletcher's check came back asking a person (findings nobody has sent to
  // Wright yet, or a check that could not test): nothing moves until someone
  // decides, so it prints under Needs you, as the sheet's pill says.
  if (checkResultsOf(row)?.asksPerson) return "needs";
  // A live split waits on a person while a ticket of it does, as its roll-up
  // says ("part 2 waiting on you"); it prints there with its tickets under it
  // (YAG-1113).
  const rollUp = splitRollUpOf(driver);
  if (rollUp && splitLeadOf(rollUp)?.state === "waiting") return "needs";
  switch (state) {
    case "waiting":
      if (isReadyToMerge(driver)) return "ready";
      if (isStoppedAsk(driver)) return "stop";
      return "needs";
    case "working":
      return "live";
    case "stopped":
      return "stop";
    case "queued": {
      // Approved on GitHub with no ask open: only a person's merge is left.
      if (isApprovedReadyToMerge(driver)) return "ready";
      const { mode } = stagePieOf(driver);
      return mode === "stop" || mode === "failed" ? "stop" : "live";
    }
    case "settled":
      return "done";
  }
}

/**
 * The group the ticket sheet's pill names: the row's own group, so the pill
 * and the row can never disagree (YAG-1079). A record that prints in no group
 * (a pull request review nobody filed a ticket for) reads its own state.
 */
export function sheetGroupOf(row: PipelineRow, now: number): QueueGroup {
  const group = queueGroupOf(row, now);
  if (group) return group;
  switch (rowStateOf(driverOf(row))) {
    case "waiting":
      return "needs";
    case "stopped":
      return "stop";
    case "settled":
      return "done";
    default:
      return "live";
  }
}

/** A settled row whose last activity is since the reader's midnight: Done's "N today", All teams' Done. */
export function isSettledToday(row: PipelineRow, now: number): boolean {
  const driver = driverOf(row);
  return rowStateOf(driver) === "settled" && timeOf(activityAtOf(driver)) >= startOfDay(now);
}

/** Whether the search names this row: its ticket, or the review or QA case folded onto it. */
export function rowMatchesSearch(row: PipelineRow, query: WorkSearchQuery): boolean {
  return [row.anchor, row.review, row.qa].some(
    (item) =>
      item != null &&
      workSearchReasonOf(
        {
          title: item.title,
          identifier: item.identifier,
          trackerKey: item.trackerKey,
          prNumber: item.prNumber,
          prBranch: item.prBranch ?? item.branchName,
        },
        query,
      ) != null,
  );
}

/**
 * A Team's archived tickets, which only a search prints. The fold runs over
 * every item first, so an archived review lands on its ticket (a shipped
 * ticket's review is archived with it) instead of standing as a row of its own.
 * Newest archived first.
 */
export function archivedRows(items: readonly WorkItem[]): PipelineRow[] {
  return foldPipelineRows([...items])
    .filter((row) => row.anchor.archivedAt && !isStandaloneReview(row))
    .sort((a, b) => timeOf(b.anchor.archivedAt) - timeOf(a.anchor.archivedAt));
}

/* ── a split's family (YAG-1113) ───────────────────────────────────────── */

/** A ticket of a split, printed under its parent's row. */
export interface QueuePart {
  row: PipelineRow;
  /** Its own group, by its own state: its verb, its ring and its age follow it. */
  group: QueueGroup;
  place: SplitPartPlace;
}

/** Which listed rows print under which. */
export interface SplitNesting {
  /** Under each listed parent, the tickets of its split this list carries, in split order. */
  parts: ReadonlyMap<string, readonly PipelineRow[]>;
  /** Each nested ticket's part, read against the tickets listed with it. */
  places: ReadonlyMap<string, SplitPartPlace>;
  /** The row each nested ticket's family prints under: its parent, or that parent's own. */
  rootOf: ReadonlyMap<string, string>;
}

function splitOrderOf(a: PipelineRow, b: PipelineRow): number {
  const part = (row: PipelineRow) => readSplitPlace(row.anchor)?.part ?? Number.MAX_SAFE_INTEGER;
  return (
    part(a) - part(b) ||
    timeOf(a.anchor.createdAt) - timeOf(b.anchor.createdAt) ||
    a.anchor.id.localeCompare(b.anchor.id)
  );
}

/**
 * A split's tickets under their parent's row (GPX ENG-3033: "I want these to
 * be more clearly a child of 3033"). A ticket nests only under a parent this
 * list carries, whose split's tickets were read (`splitProgress`: a parent
 * whose read failed reads closed, and its tickets stand on their own as
 * before), and that still counts it (`splitInto`: a ticket a Start over
 * replaced was let go). A ticket that was split again nests its own tickets.
 */
export function nestSplits(rows: readonly PipelineRow[]): SplitNesting {
  const byId = new Map(rows.map((row) => [row.anchor.id, row]));
  const parentOf = new Map<string, string>();
  for (const row of rows) {
    const parentId = readSplitPlace(row.anchor)?.parentId;
    const parent = parentId ? byId.get(parentId) : undefined;
    if (!parent || parent === row || parent.anchor.splitProgress == null) continue;
    const into = parent.anchor.sourceRef?.splitInto;
    if (Array.isArray(into) && !into.includes(row.anchor.id)) continue;
    parentOf.set(row.anchor.id, parent.anchor.id);
  }
  // A ticket whose parents lead back to it nests nowhere.
  for (const id of [...parentOf.keys()]) {
    const seen = new Set<string>();
    for (let at: string | undefined = id; at !== undefined; at = parentOf.get(at)) {
      if (seen.has(at)) {
        parentOf.delete(id);
        break;
      }
      seen.add(at);
    }
  }
  const rootOf = new Map<string, string>();
  const parts = new Map<string, PipelineRow[]>();
  for (const [id, parentId] of parentOf) {
    let root = parentId;
    for (let up = parentOf.get(root); up !== undefined; up = parentOf.get(root)) root = up;
    rootOf.set(id, root);
    const list = parts.get(parentId) ?? [];
    list.push(byId.get(id)!);
    parts.set(parentId, list);
  }
  const places = new Map<string, SplitPartPlace>();
  for (const [parentId, list] of parts) {
    list.sort(splitOrderOf);
    const parent = byId.get(parentId)!.anchor;
    const siblings = list.map((row) => row.anchor);
    for (const row of list) {
      places.set(row.anchor.id, splitPartPlaceOf(row.anchor, parent, siblings));
    }
  }
  return { parts, places, rootOf };
}

/** The split's tickets under each of `heads`, and under those, as the rows print. */
function partsUnder(
  heads: readonly PipelineRow[],
  nesting: SplitNesting,
  groupOf: ReadonlyMap<string, QueueGroup>,
): Map<string, QueuePart[]> {
  const out = new Map<string, QueuePart[]>();
  const visit = (id: string) => {
    const under: QueuePart[] = [];
    for (const row of nesting.parts.get(id) ?? []) {
      const group = groupOf.get(row.anchor.id);
      const place = nesting.places.get(row.anchor.id);
      if (group && place) under.push({ row, group, place });
    }
    if (under.length === 0) return;
    out.set(id, under);
    for (const part of under) visit(part.row.anchor.id);
  };
  for (const head of heads) visit(head.anchor.id);
  return out;
}

/**
 * Which group a split's family prints in: the one among its tickets' own
 * that most needs the reader, so a ticket that asks a person never hides
 * under Working, and one that stopped never under Done.
 */
const FAMILY_RANK: readonly QueueGroup[] = ["needs", "ready", "stop", "live", "done"];

export interface QueueGroupRows {
  group: QueueGroup;
  /** The rows the group prints at its top level: a split's family is its parent's row. */
  rows: PipelineRow[];
  /** A split's tickets under their parent's row (YAG-1113), by the parent's id. */
  parts: ReadonlyMap<string, readonly QueuePart[]>;
}

/**
 * The Team queue: the five groups in order, each sorted the way the reader
 * reads it. Needs you runs in pipeline order (proposals, then plans, then
 * builds, then reviews) so asks of one kind sit together, oldest first inside
 * each stage. Ready to merge is oldest ask first (the one that has waited
 * longest is the one to answer); Working, Stopped and Done are most recent
 * first.
 *
 * A split prints once, as its family (YAG-1113): the parent's row, then its
 * tickets in split order under it, in the group that most needs the reader
 * among them, where the first of them that group sorts would stand. A group
 * counts its rows, so the family counts once, as the one ticket it is on the
 * tracker.
 */
export function teamQueueGroups(rows: readonly PipelineRow[], now: number): QueueGroupRows[] {
  const buckets = new Map<QueueGroup, PipelineRow[]>(QUEUE_GROUPS.map((group) => [group, []]));
  const groupOf = new Map<string, QueueGroup>();
  for (const row of rows) {
    const group = queueGroupOf(row, now);
    if (!group) continue;
    groupOf.set(row.anchor.id, group);
    buckets.get(group)!.push(row);
  }
  const oldestAsk = (a: PipelineRow, b: PipelineRow) =>
    timeOf(waitingSinceOf(driverOf(a))) - timeOf(waitingSinceOf(driverOf(b)));
  const recent = (a: PipelineRow, b: PipelineRow) =>
    timeOf(activityAtOf(driverOf(b))) - timeOf(activityAtOf(driverOf(a)));
  const stageIndex = (row: PipelineRow) => PIPELINE_STAGES.indexOf(pipelineStageOf(driverOf(row)));
  const byStage = (a: PipelineRow, b: PipelineRow) =>
    stageIndex(a) - stageIndex(b) || oldestAsk(a, b);
  const SORT: Record<QueueGroup, (a: PipelineRow, b: PipelineRow) => number> = {
    needs: byStage,
    ready: oldestAsk,
    live: recent,
    stop: recent,
    done: recent,
  };

  const listed = rows.filter((row) => groupOf.has(row.anchor.id));
  const byId = new Map(listed.map((row) => [row.anchor.id, row]));
  const nesting = nestSplits(listed);
  const rank = (group: QueueGroup | undefined) =>
    group ? FAMILY_RANK.indexOf(group) : FAMILY_RANK.length;
  const familyGroup = new Map<string, QueueGroup>();
  for (const [id, rootId] of nesting.rootOf) {
    const held = familyGroup.get(rootId) ?? groupOf.get(rootId);
    const own = groupOf.get(id);
    const best = rank(own) < rank(held) ? own : held;
    if (best) familyGroup.set(rootId, best);
  }

  return QUEUE_GROUPS.map((group) => {
    const top: PipelineRow[] = [];
    const placed = new Set<string>();
    for (const row of [...buckets.get(group)!].sort(SORT[group])) {
      const rootId = nesting.rootOf.get(row.anchor.id) ?? row.anchor.id;
      if (placed.has(rootId) || (familyGroup.get(rootId) ?? group) !== group) continue;
      const head = byId.get(rootId);
      if (!head) continue;
      placed.add(rootId);
      top.push(head);
    }
    return { group, rows: top, parts: partsUnder(top, nesting, groupOf) };
  });
}

/**
 * The Team queue as a search leaves it: each group's matches, a split's
 * ticket under its parent's row, and every ticket under a parent the search
 * found.
 */
export function searchQueueGroups(
  groups: readonly QueueGroupRows[],
  query: WorkSearchQuery,
): QueueGroupRows[] {
  return groups.map((group) => {
    const parts = new Map<string, readonly QueuePart[]>();
    const keepAll = (id: string) => {
      const under = group.parts.get(id);
      if (!under) return;
      parts.set(id, under);
      for (const part of under) keepAll(part.row.anchor.id);
    };
    const keep = (row: PipelineRow): boolean => {
      if (rowMatchesSearch(row, query)) {
        keepAll(row.anchor.id);
        return true;
      }
      const kept = (group.parts.get(row.anchor.id) ?? []).filter((part) => keep(part.row));
      if (kept.length > 0) parts.set(row.anchor.id, kept);
      return kept.length > 0;
    };
    return { group: group.group, rows: group.rows.filter(keep), parts };
  });
}

/**
 * The asks of Needs you in the order it prints them, for the sheet's next
 * ask: a split's under its parent, and never a parent that only rolls its
 * tickets' asks up.
 */
export function needsOrderOf(group: QueueGroupRows): string[] {
  const out: string[] = [];
  const visit = (row: PipelineRow, own: QueueGroup) => {
    if (own === "needs" && !splitRollUpOf(driverOf(row))) out.push(row.anchor.id);
    for (const part of group.parts.get(row.anchor.id) ?? []) visit(part.row, part.group);
  };
  for (const row of group.rows) visit(row, group.group);
  return out;
}

export interface TeamWaitingGroup {
  deskId: string;
  /** Every ask, in the order the group prints them. */
  rows: PipelineRow[];
  /** What prints at the top: each ask, or the parent a split's asks print under. */
  heads: PipelineRow[];
  /** A split's asks under their parent's row, in split order (YAG-1113). */
  parts: ReadonlyMap<string, readonly QueuePart[]>;
}

/**
 * Waiting on you: every ask addressed to the reader (`isWaitingOnViewer`, the
 * rule the rail's pill counts), one group per Team in the order the Teams are
 * given, oldest ask first inside each. A Team with nothing waiting prints
 * nothing; an ask on a Team the list does not name is dropped, because the
 * rail would not name it either.
 *
 * A split's asks print under their parent's row, where its oldest would
 * stand (YAG-1113). The parent is there to say whose ticket it is, not as an
 * ask: only its own ask, when it has one, counts.
 */
export function waitingByTeam(
  rows: readonly PipelineRow[],
  deskIds: readonly string[],
  viewerUserId: string | null,
): TeamWaitingGroup[] {
  const byDesk = new Map<string, PipelineRow[]>();
  for (const row of rows) {
    const driver = driverOf(row);
    if (!isWaitingOnViewer(driver, viewerUserId)) continue;
    const deskId = row.anchor.deskId;
    if (!deskId) continue;
    const bucket = byDesk.get(deskId);
    if (bucket) bucket.push(row);
    else byDesk.set(deskId, [row]);
  }
  const oldestFirst = (a: PipelineRow, b: PipelineRow) =>
    timeOf(waitingSinceOf(driverOf(a))) - timeOf(waitingSinceOf(driverOf(b)));
  const nesting = nestSplits(rows);
  const byId = new Map(rows.map((row) => [row.anchor.id, row]));
  // Where each nested ticket prints under its family's head, its own tickets after it.
  const printAt = new Map<string, number>();
  const number = (id: string) => {
    for (const row of nesting.parts.get(id) ?? []) {
      printAt.set(row.anchor.id, printAt.size);
      number(row.anchor.id);
    }
  };
  for (const id of nesting.parts.keys()) if (!nesting.rootOf.has(id)) number(id);
  const at = (part: QueuePart) => printAt.get(part.row.anchor.id) ?? 0;

  return deskIds.flatMap((deskId) => {
    const asks = [...(byDesk.get(deskId) ?? [])].sort(oldestFirst);
    if (asks.length === 0) return [];
    const heads: PipelineRow[] = [];
    const parts = new Map<string, QueuePart[]>();
    const lead = (row: PipelineRow) => {
      if (!heads.includes(row)) heads.push(row);
    };
    for (const row of asks) {
      const rootId = nesting.rootOf.get(row.anchor.id);
      const root = rootId ? byId.get(rootId) : undefined;
      const place = nesting.places.get(row.anchor.id);
      if (!rootId || !root || !place) {
        lead(row);
        continue;
      }
      lead(root);
      parts.set(rootId, [...(parts.get(rootId) ?? []), { row, group: "needs", place }]);
    }
    for (const under of parts.values()) under.sort((a, b) => at(a) - at(b));
    const asking = new Set(asks);
    const printed = heads.flatMap((head) => [
      ...(asking.has(head) ? [head] : []),
      ...(parts.get(head.anchor.id) ?? []).map((part) => part.row),
    ]);
    return [{ deskId, rows: printed, heads, parts }];
  });
}

/** The verb a row's hover button names, by the group it prints in. */
export function rowVerbOf(row: PipelineRow, group: QueueGroup): RowVerb {
  // A split's row only opens the parent; each act is on the ticket that asks (YAG-1113).
  if (splitRollUpOf(driverOf(row))) return "Open";
  switch (group) {
    case "needs":
      return driverOf(row).currentGateKind === "needs_input" ? "Answer" : "Review";
    case "ready": {
      // A draft is neither approved nor merged yet: the sheet names its step.
      // Approved with no ask open: the merge is the step.
      if (!isNeedsYou(driverOf(row))) return "Merge";
      const ask = gateAskOf(driverOf(row));
      if (ask.draft === true) return "Open";
      return readyAskNeedsApproval(ask) ? "Approve" : "Merge";
    }
    case "live":
      // Waiting its turn is in Working too, with nothing yet to watch.
      return rowStateOf(driverOf(row)) === "working" ? "Watch" : "Open";
    case "stop":
      // A stop that asked still has a question to answer.
      return isStoppedAsk(driverOf(row)) && !isSettledInfraStop(driverOf(row)) ? "Answer" : "Why";
    case "done":
      return "Receipt";
  }
}

/** The key a row prints: the tracker's key when the ticket has one, else ours. Never both. */
export function rowKeyOf(item: WorkItem): string {
  return item.trackerKey || item.identifier;
}

/** The stage owner's name, which a row's sentence reads ("Wright is working"). */
export function stageOwnerName(
  driver: WorkItem,
  workerOf: StageOwnerLookup | undefined,
): string | null {
  return stageOwnerOf(driver, workerOf)?.displayName ?? null;
}

/** The worker the row names for where the item stands (see {@link stageOwnerName}). */
export function stageOwnerOf(
  driver: WorkItem,
  workerOf: StageOwnerLookup | undefined,
): WorkerSummary | null {
  if (!workerOf) return null;
  // A fix round in Review is the builder's run, whatever craft owns the stage.
  const craft = isFixingInReview(driver)
    ? PIPELINE_STAGE_CRAFT.build
    : PIPELINE_STAGE_CRAFT[pipelineStageOf(driver)];
  return (
    (craft ? workerOf.byCraft(craft, driver.personaSlug) : null) ?? workerOf(driver.personaSlug)
  );
}

/**
 * The row's state line without its stage word (the row prints that in the
 * stage's own hue before it): "Wright is working", "waiting on
 * you to approve Reeve's plan", "stopped: build timed out".
 */
export function rowSentenceOf(
  driver: WorkItem,
  workerName: string | null,
  viewerUserId: string | null,
): string {
  const label = stageLabelOf(driver);
  const line = rowStateLine(driver, workerName, viewerUserId);
  let clause = line === label ? "" : line;
  if (line.startsWith(`${label} · `)) clause = line.slice(label.length + 3);
  if (!clause && isNeedsYou(driver)) clause = "waiting on you";
  if (!clause && rowStateOf(driver) === "settled") clause = "shipped";
  // No "attempt N": `attempt_no` counts every run on the item, planning
  // included, so "Wright is working · attempt 3" read as two failed builds
  // on a first build (GPX, 2026-10-29). The live stream stopped printing it
  // for the same reason (`work/LiveStream.tsx`).
  return clause;
}

/**
 * THE TICKET'S LINE on a queue row (YAG-1079): {@link rowSentenceOf} of the
 * item driving the row, except where the row has more to say than one item.
 *
 *   two at work      "Proctor is reviewing · Fletcher is testing", one clause
 *                    per Agent working on the ticket now
 *   results are in   "Proctor: would approve · Fletcher: found problems"
 *
 * The ticket sheet's Now block and its pill read the same facts.
 */
export function ticketSentenceOf(
  row: PipelineRow,
  workerOf: StageOwnerLookup | undefined,
  viewerUserId: string | null,
): string {
  const working = workingItemsOf(row);
  if (working.length > 1) {
    return working
      .map((item) => rowClauseOf(item, stageOwnerName(item, workerOf), viewerUserId))
      .filter(Boolean)
      .join(" · ");
  }
  const results = checkResultsOf(row);
  if (results) {
    return checkResultsLine(results, {
      reviewer: checkerName(row.review, PIPELINE_STAGE_CRAFT.review, workerOf),
      tester: checkerName(row.qa, PIPELINE_STAGE_CRAFT.qa, workerOf),
    });
  }
  const driver = driverOf(row);
  return rowSentenceOf(driver, stageOwnerName(driver, workerOf), viewerUserId);
}

/** The Agent who carries a check's craft, by the case's own worker when there is one. */
function checkerName(
  facet: WorkItem | null,
  craft: string | null,
  workerOf: StageOwnerLookup | undefined,
): string | null {
  if (!workerOf) return null;
  return (
    (facet ? workerOf(facet.personaSlug)?.displayName : null) ??
    (craft ? workerOf.byCraft(craft, facet?.personaSlug)?.displayName : null) ??
    null
  );
}

/**
 * A row that closed with no work landed (dismissed, or split into other
 * tickets) reads CLOSED where a shipped one reads DONE, and drops the
 * sentence's own "closed, ": "CLOSED · no work was done". Null for any other
 * row. The ticket sheet keeps `rowSentenceOf` as it is, under its own stage.
 */
export function closedRowWords(
  driver: WorkItem,
  sentence: string,
): { tag: { label: string; color: string }; state: string } | null {
  if (stageOutcomeOf(driver) !== "canceled") return null;
  return {
    tag: { label: "Closed", color: "var(--text-tertiary)" },
    state: sentence.replace(/^closed, /, ""),
  };
}

/** The age a row prints: how long it has been like this, in the queue's short form. */
export function rowAgeIso(row: PipelineRow, group: QueueGroup): string {
  const driver = driverOf(row);
  if (group === "needs" || group === "ready" || isStoppedAsk(driver)) return waitingSinceOf(driver);
  if (group === "live" && driver.activeRun?.startedAt) return driver.activeRun.startedAt;
  return rowAgeOf(driver);
}

/** "2h 10m", "14m", "3d", "<1m": a length of time, in the row's mono. */
export function shortSpan(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000));
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest ? `${hours}h ${rest}m` : `${hours}h`;
  }
  return `${Math.floor(hours / 24)}d`;
}

/** "2h 10m", "14m", "3d": a duration since `iso`, in the row's mono. */
export function shortAge(iso: string | null | undefined, now: number): string {
  const at = timeOf(iso);
  if (!at) return "";
  if (now - at < 60_000) return "now";
  return shortSpan(now - at);
}

/**
 * "22m ago": how long since something happened, said as a time ago. A bare
 * "22m" beside a ticket read as how long its run took (GPX, 2026-10-29);
 * only a live run's own clock prints bare, because that is what it is.
 */
export function agoLabel(iso: string | null | undefined, now: number): string {
  const age = shortAge(iso, now);
  return age && age !== "now" ? `${age} ago` : age;
}

/** The moment itself, for a time's tooltip: "Oct 29, 2026, 2:15 PM". */
export function absoluteTime(iso: string | null | undefined): string {
  const at = timeOf(iso);
  if (!at) return "";
  return new Date(at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/**
 * The age a queue row prints, and its tooltip. A row whose read carried the
 * ticket's clocks (YAG-1117) says what the time was spent on: Needs you
 * "worked 22m · waiting on you 8h 11m", Working "worked 22m · running 4m",
 * Done "worked 47m", frozen, with the finish in the tooltip. "GPX-20 has
 * taken 8h 11m" counted a night's sleep on a plan as the build's time. A row
 * without them reads as before: a live row's age is its run's clock ("22m",
 * running for 22 minutes); every other row's is a time ago ("22m ago"). The
 * tooltip names the moment either way.
 */
export function rowWhenOf(
  row: PipelineRow,
  group: QueueGroup,
  now: number,
): { iso: string; text: string; title: string } {
  const iso = rowAgeIso(row, group);
  const at = absoluteTime(iso);
  const driver = driverOf(row);
  const running = group === "live" && Boolean(driver.activeRun?.startedAt);
  const clock = driver.clock ?? null;
  if (clock) {
    const worked = clock.workedMs > 0 ? `worked ${shortSpan(clock.workedMs)}` : "";
    const totals = clockTotalsOf(clock, now);
    if (group === "needs" && (clock.waitingSince || totals.waitingOnPersonMs > 0)) {
      const wait = `waiting on you ${shortSpan(totals.waitingOnPersonMs)}`;
      return { iso, text: joinWhen(worked, wait), title: at ? `Waiting since ${at}` : "" };
    }
    if (running) return runningWhen(iso, at, now, worked);
    if (group === "done" && worked) {
      return { iso, text: worked, title: at ? `Finished ${at}` : "" };
    }
    // A row with no ask open (a stop notice) reads its time ago, after what it worked.
    return { iso, text: joinWhen(worked, agoLabel(iso, now)), title: at };
  }
  if (running) return runningWhen(iso, at, now, null);
  return { iso, text: agoLabel(iso, now), title: at };
}

function joinWhen(...parts: string[]): string {
  return parts.filter(Boolean).join(" · ");
}

/**
 * A live row's clock, "running for 22m" in the tooltip. With the ticket's
 * clocks the text says "running 22m" after what it worked earlier (`before`,
 * "" for nothing); without them (`null`) the bare clock, as before.
 */
function runningWhen(
  iso: string,
  at: string,
  now: number,
  before: string | null,
): { iso: string; text: string; title: string } {
  const span = shortAge(iso, now);
  const lasted = span === "now" ? "" : ` for ${span},`;
  const title = at ? `Running${lasted} since ${at}` : "";
  if (before === null) return { iso, text: span, title };
  return { iso, text: joinWhen(before, `running ${span === "now" ? "<1m" : span}`), title };
}

/**
 * A run as {@link planningSpanOf} reads it: the fields it reads off an entry
 * of the sheet's runs list (`/runs-view`), typed from that entry so the two
 * cannot drift apart.
 */
export type PlanningRunFacts = Pick<
  WorkItemRunListEntry,
  "purpose" | "startedAt" | "finishedAt" | "lane" | "sessions"
>;

/**
 * How long the plan at an open plan gate took to write, in milliseconds, or
 * null when the runs do not say. A one-attempt lane run's plan is its newest
 * plan session, which ends when the gate opens and, when it is the attempt's
 * first, starts with the run (the machine's preparing is the plan's time
 * too); any other plan is the newest planning run that finished by the time
 * the gate opened.
 */
export function planningSpanOf(
  runs: ReadonlyArray<PlanningRunFacts>,
  gateOpenedAt: string | null | undefined,
): number | null {
  const gate = timeOf(gateOpenedAt);
  if (!gate) return null;
  // Two clocks: a run's stamps can land a moment after the gate's.
  const slack = 5_000;
  let best: { start: number; end: number } | null = null;
  // The newest plan wins: the latest end, then the latest start.
  const consider = (start: number, end: number) => {
    if (!best || end > best.end || (end === best.end && start > best.start)) best = { start, end };
  };
  for (const run of runs) {
    if (run.lane === "attempt") {
      const runStart = timeOf(run.startedAt);
      (run.sessions ?? []).forEach((session, index) => {
        const sessionStart = timeOf(session.startedAt);
        if (session.stage !== "plan" || !sessionStart || sessionStart > gate) return;
        // The attempt's first session starts once its machine is prepared;
        // its plan took from the run's own start (YAG-1096: "planned in 5m"
        // over a run of 9).
        const start = index === 0 && runStart && runStart <= sessionStart ? runStart : sessionStart;
        consider(start, gate);
      });
      continue;
    }
    if (run.purpose !== "planning") continue;
    const start = timeOf(run.startedAt);
    const end = timeOf(run.finishedAt);
    if (start && end && end >= start && end <= gate + slack) consider(start, end);
  }
  const found = best as { start: number; end: number } | null;
  return found ? found.end - found.start : null;
}
